import React, { useState, useEffect, useCallback } from 'react'
import { graphql } from 'gatsby'

import type { MediaComponent, DrupalMediaBase } from './types'

import ResponsiveEmbed, { AspectRatio } from './ResponsiveEmbed'
import { breakpointValues } from '../../styles/variables'

export interface MediaVideo extends DrupalMediaBase {
	__typename: 'media__video'
	desktop: string
	mobile: string
	field_oembed_hash: {
		mobile: string
		desktop: string
	}
	mobile_resolution: string
	thumbnail: {
		width: number
		height: number
	}
}

type VideoType = keyof MediaVideo['field_oembed_hash']

function makeOEmbedUrl(media: MediaVideo, fieldName: VideoType) {
	const url = new URL(`${process.env.GATSBY_DRUPAL_HOST!}/media/oembed`)

	url.searchParams.set('url', media[fieldName])
	url.searchParams.set('hash', media.field_oembed_hash[fieldName])

	return url.toString()
}

function getVideoAspectRatio(media: MediaVideo, type: string): number | AspectRatio {
	if (type === 'desktop') {
		return media.thumbnail.height / media.thumbnail.width
	}

	return media.mobile_resolution as AspectRatio
}

const MediaVideoComponent: MediaComponent<MediaVideo> = ({ className, media, title }) => {
	const [type, setType] = useState<undefined | VideoType>(() => {
		// Use desktop media by default if mobile video is not set.
		// Mobile video is not a required field.
		if (!media.mobile) {
			return 'desktop'
		}
	})

	const pickVideo = useCallback(() => {
		if (!media.mobile || window.matchMedia(`(min-width: ${breakpointValues.md}px)`).matches) {
			return setType('desktop')
		}

		return setType('mobile')
	}, [])

	/**
	 * @todo: This might need to be layoutEffect?
	 */
	useEffect(() => {
		window.addEventListener('resize', pickVideo)

		pickVideo()

		return () => window.removeEventListener('resize', pickVideo)
	}, [pickVideo, media])

	return (
		<ResponsiveEmbed className={className} aspectRatio={type && getVideoAspectRatio(media, type)}>
			<iframe title={title} src={type && makeOEmbedUrl(media, type)} frameBorder="0" />
		</ResponsiveEmbed>
	)
}

export default MediaVideoComponent

export const query = graphql`
	fragment MediaVideo on media__video {
		__typename
		mid: drupal_internal__mid
		desktop: field_media_oembed_video
		mobile: field_oembed_url_mobile
		field_oembed_hash {
			desktop: field_media_oembed_video
			mobile: field_oembed_url_mobile
		}
		mobile_resolution: field_options
		thumbnail {
			height
			width
		}
	}
`
